/**
 * @architecture-header 1
 * @layer module
 * @domain organization-invitation-acceptance
 * @responsibility Present the SET-F06 unauthenticated invitation preview, the portal login hand-off,
 *   and the authenticated accept/decline outcome states.
 * @state-owner component-local preview and acceptance state
 * @allowed-imports React; React Router; workspace environment/session; settings-invitations module
 *   public API; shared loading; auth public screen
 * @forbidden shared router registration; direct database access; client authorization decisions
 * @public-api OrganizationInvitationAcceptancePage and OrganizationInvitationAcceptanceEntry
 * @side-effects unauthenticated preview and authenticated accept/decline commands
 * @tests OrganizationInvitationAcceptancePage.test.tsx and SET-F06 Playwright flow
 */

import { VispoApiError } from '@vispo/api-client';
import type { OrganizationInvitationPreviewResponse } from '@vispo/contracts';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useIsMobile } from '../../../shared/model/use-workspace';
import { SignInScreen } from '../../auth';
import {
  acceptOrganizationInvitation,
  declineOrganizationInvitation,
  previewOrganizationInvitation,
} from '../../settings-invitations';
import './organization-invitation-acceptance.css';

interface AcceptanceState {
  phase: 'declined' | 'login' | 'pending' | 'preview' | 'refusal' | 'success';
  message?: string;
  preview?: OrganizationInvitationPreviewResponse;
}

const ROLE_LABEL: Record<string, string> = { admin: '管理者', member: '成員', staff: '職員' };

function refusalMessage(error: unknown): string {
  if (error instanceof VispoApiError) {
    if (error.code === 'ORGANIZATION_INVITATION_FORBIDDEN') {
      return '這個邀請不是寄給目前登入的帳號，請用受邀的 email 登入。';
    }
    if (error.code === 'ORGANIZATION_INVITATION_STATE_CONFLICT') {
      return '這個邀請已經處理過，或已經過期。';
    }
    if (error.code === 'ORGANIZATION_INVITATION_NOT_FOUND') {
      return '找不到這個邀請，請確認連結完整。';
    }
  }
  return '這個連結無法使用，請確認連結完整，或請對方重新發送。';
}

export function OrganizationInvitationAcceptancePage({
  mobile,
  token,
}: {
  mobile: boolean;
  token: string;
}) {
  const { apiClient } = useWorkspaceEnvironment();
  const { isLoading, session } = useWorkspaceSession();
  const [state, setState] = useState<AcceptanceState>({ phase: 'pending' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    // Deliberately unconditional: preview requires no session at all (mirrors the backend's
    // preview_organization_invitation handler, which omits ActorDependency on purpose).
    void previewOrganizationInvitation(apiClient, { token })
      .then((preview) => {
        if (active) setState({ phase: 'preview', preview });
      })
      .catch((error: unknown) => {
        if (active) setState({ phase: 'refusal', message: refusalMessage(error) });
      });
    return () => {
      active = false;
    };
  }, [apiClient, token]);

  const className = `invitation-acceptance${mobile ? ' invitation-acceptance--mobile' : ' invitation-acceptance--desktop'}`;
  const surface = mobile ? 'mobile' : 'desktop';

  if (state.phase === 'pending') {
    return <VispoLoading className="workspace-loading" label="正在確認邀請內容…" />;
  }

  if (state.phase === 'refusal') {
    return (
      <main className={className} data-surface={surface}>
        <section className="invitation-acceptance__card" role="alert">
          <h1>連結無法使用</h1>
          <p>{state.message}</p>
          <a className="bt" href="/teacher/login">
            回到登入頁
          </a>
        </section>
      </main>
    );
  }

  if (state.phase === 'success') {
    return (
      <main className={className} data-surface={surface}>
        <section
          aria-live="polite"
          className="invitation-acceptance__card invitation-acceptance__card--success"
        >
          <span aria-hidden="true" className="invitation-acceptance__check">
            ✓
          </span>
          <h1>已加入組織</h1>
          <p>你現在可以在 Vispo 工作區查看這個組織。</p>
          <a className="bt pri" href="/teacher/workspace">
            前往我的工作區
          </a>
        </section>
      </main>
    );
  }

  if (state.phase === 'declined') {
    return (
      <main className={className} data-surface={surface}>
        <section aria-live="polite" className="invitation-acceptance__card">
          <h1>已拒絕邀請</h1>
          <p>這個邀請不會加入這個組織。</p>
          <a className="bt" href="/teacher/workspace">
            回到我的工作區
          </a>
        </section>
      </main>
    );
  }

  if (state.phase === 'login') {
    return <SignInScreen audience="teacher" returnTo={`/organization-invitations/${token}`} />;
  }

  const preview = state.preview;
  const onAccept = () => {
    if (!preview) return;
    setBusy(true);
    void acceptOrganizationInvitation(apiClient, preview.id, {
      idempotencyKey: `invitation-accept-${preview.id}`,
      token,
    })
      .then(() => setState({ phase: 'success', preview }))
      .catch((error: unknown) =>
        setState({ message: refusalMessage(error), phase: 'refusal', preview }),
      )
      .finally(() => setBusy(false));
  };
  const onDecline = () => {
    if (!preview) return;
    setBusy(true);
    void declineOrganizationInvitation(apiClient, preview.id, {
      idempotencyKey: `invitation-decline-${preview.id}`,
      token,
    })
      .then(() => setState({ phase: 'declined', preview }))
      .catch((error: unknown) =>
        setState({ message: refusalMessage(error), phase: 'refusal', preview }),
      )
      .finally(() => setBusy(false));
  };

  return (
    <main className={className} data-surface={surface}>
      <section
        aria-labelledby="invitation-acceptance-title"
        className="invitation-acceptance__card"
      >
        <span className="invitation-acceptance__eyebrow">組織邀請</span>
        <h1 id="invitation-acceptance-title">確認加入組織</h1>
        <div className="invitation-acceptance__preview">
          <span>組織</span>
          <strong>{preview?.organizationName}</strong>
          <span>身分</span>
          <strong>{ROLE_LABEL[preview?.invitedRole ?? 'member']}</strong>
        </div>
        <p>請確認組織與身分，接受後即可取得對應的組織權限。</p>
        {isLoading ? (
          <VispoLoading className="invitation-acceptance__loading" label="正在確認登入狀態…" />
        ) : session ? (
          <div className="invitation-acceptance__actions">
            <button className="bt pri" disabled={busy} type="button" onClick={onAccept}>
              {busy ? '正在加入…' : '接受邀請'}
            </button>
            <button className="bt" disabled={busy} type="button" onClick={onDecline}>
              拒絕邀請
            </button>
          </div>
        ) : (
          <div className="invitation-acceptance__actions">
            <button
              className="bt pri"
              type="button"
              onClick={() => setState((current) => ({ ...current, phase: 'login' }))}
            >
              登入以接受邀請
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

export function OrganizationInvitationAcceptanceEntry() {
  const { token = '' } = useParams<{ token: string }>();
  const mobile = useIsMobile();
  return <OrganizationInvitationAcceptancePage mobile={mobile} token={token} />;
}
